import React, { useState, useCallback } from 'react'; // Added React import
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';

// Define PopupComponent outside the hook
const PopupComponent = ({ config, onClose }) => {
  if (!config) {
    return null;
  }

  const { title, message, type = 'info', confirmText = 'OK', cancelText = 'Anuluj', onConfirm, onCancel } = config;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    console.log("PopupComponent: handleConfirm called"); // Debug log
    if (onConfirm) {
        console.log("PopupComponent: Calling onConfirm"); // Debug log
        onConfirm();
    } else {
        console.log("PopupComponent: onConfirm is undefined"); // Debug log
    }
    onClose();
  };

  const handleCancel = () => {
    console.log("PopupComponent: handleCancel called"); // Debug log
    if (onCancel) {
        console.log("PopupComponent: Calling onCancel"); // Debug log
        onCancel();
    } else {
        console.log("PopupComponent: onCancel is undefined"); // Debug log
    }
    onClose();
  };

  const Icon = {
    info: Info,
    success: CheckCircle,
    error: AlertTriangle,
    confirm: AlertTriangle,
    loading: () => <div className="popup-spinner"></div>
  }[type];

  // Assuming CSS classes like popup-overlay, popup-content, etc., exist
  // Add console logs to button onClick handlers
  // Conditionally add 'visible' class to the overlay based on config presence
  return (
    <div className={`popup-overlay ${config ? 'visible' : ''}`} onClick={handleOverlayClick}>
      {/* Content remains the same, but now overlay has pointer-events: auto when visible */}
      <div className={`popup-content popup-${type}`} onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          {Icon && <Icon size={24} className="popup-icon" />}
          <h3>{title || (type === 'error' ? 'Błąd' : type === 'success' ? 'Sukces' : 'Informacja')}</h3>
          <button onClick={onClose} className="popup-close-btn">&times;</button>
        </div>
        <div className="popup-body">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
        <div className="popup-footer">
          {type === 'confirm' ? (
            <>
              <button onClick={() => { console.log('Confirm button clicked'); handleConfirm(); }} className="popup-btn popup-btn-confirm">
                {confirmText}
              </button>
              <button onClick={() => { console.log('Cancel button clicked'); handleCancel(); }} className="popup-btn popup-btn-cancel">
                {cancelText}
              </button>
            </>
          ) : type !== 'loading' ? (
             <button onClick={() => { console.log('OK button clicked'); onClose(); }} className="popup-btn popup-btn-confirm">
               {confirmText}
             </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Export the component separately
export { PopupComponent };

export function useOrderDetailsUIState() {
  // --- Popup State ---
  const [popupConfig, setPopupConfig] = useState(null);
  const showPopup = useCallback((config) => {
      console.log("showPopup called with config:", config); // Debug log
      setPopupConfig(config);
  }, []);
  const hidePopup = useCallback(() => {
      console.log("hidePopup called"); // Debug log
      setPopupConfig(null);
  }, []);

  // --- Guest List Editor State ---
  const [guestsOpenMap, setGuestsOpenMap] = useState({});
  const [guestsTextMap, setGuestsTextMap] = useState({});
  const [guestsLoadingMap, setGuestsLoadingMap] = useState({});
  const handleToggleGuests = useCallback((fileId) => {
    setGuestsOpenMap((prev) => ({
      ...prev,
      [fileId]: !prev[fileId]
    }));
    // Fetching logic will be in the actions hook, triggered after toggle if needed
  }, []);

  // --- AI Interaction State ---
  const [aiStateMap, setAiStateMap] = useState({}); // { projectId: { status, message, interactionId } }
  const [showAiModalForProjectId, setShowAiModalForProjectId] = useState(null);
  const [aiRequestText, setAiRequestText] = useState("");
  const [showLogsModalConfig, setShowLogsModalConfig] = useState(null); // { projectId, interactionId } | null
  const [currentLogs, setCurrentLogs] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);

  const handleOpenAiModal = useCallback((projectId) => {
    setAiRequestText("");
    setShowAiModalForProjectId(projectId);
  }, []);

  const handleCloseAiModal = useCallback(() => {
    setShowAiModalForProjectId(null);
    setAiRequestText("");
  }, []);

  const handleShowLogs = useCallback((projectId, interactionId) => {
    // Fetching logic will be in the actions hook
    setShowLogsModalConfig({ projectId, interactionId });
    setLogsLoading(true); // Set loading state here
    setCurrentLogs("");
  }, []);

  const handleCloseLogsModal = useCallback(() => {
    setShowLogsModalConfig(null);
    setCurrentLogs("");
    setLogsLoading(false); // Ensure loading is reset
  }, []);

  // --- Email Editing State ---
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState(""); // Initial value set by data hook

  const handleEditEmailClick = useCallback((currentEmail) => {
    setEditedEmail(currentEmail || "");
    setIsEditingEmail(true);
  }, []);

  const handleCancelEditEmail = useCallback((originalEmail) => {
    setIsEditingEmail(false);
    setEditedEmail(originalEmail || "");
  }, []);

  // --- Copy Link State ---
  const [copySuccessMessage, setCopySuccessMessage] = useState("");

  // --- Template Search State ---
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");

  // --- File Action States ---
  const [duplicatingFileId, setDuplicatingFileId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [addingProjectFromTemplateId, setAddingProjectFromTemplateId] = useState(null);
  const [isUploading, setIsUploading] = useState(false); // For DropZone indication

  // --- Metadata Modal State ---
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  const handleShowMetadata = useCallback((metadata) => {
      setSelectedMetadata(metadata);
      setShowMetadataModal(true);
  }, []);

  const handleCloseMetadata = useCallback(() => {
      setShowMetadataModal(false);
      setSelectedMetadata(null);
  }, []);

  // --- Missing Font State ---
  const [missingFontToAdd, setMissingFontToAdd] = useState(null);
  const [missingFontFile, setMissingFontFile] = useState(null);


  return {
    // Popup
    popupConfig,
    showPopup,
    hidePopup,
    // PopupComponent is no longer returned here

    // Guest List
    guestsOpenMap,
    setGuestsOpenMap, // Allow direct setting if needed by actions
    guestsTextMap,
    setGuestsTextMap,
    guestsLoadingMap,
    setGuestsLoadingMap,
    handleToggleGuests,

    // AI Interactions
    aiStateMap,
    setAiStateMap,
    showAiModalForProjectId,
    aiRequestText,
    setAiRequestText,
    showLogsModalConfig,
    currentLogs,
    setCurrentLogs, // Allow actions hook to set logs
    logsLoading,
    setLogsLoading, // Allow actions hook to manage loading
    handleOpenAiModal,
    handleCloseAiModal,
    handleShowLogs,
    handleCloseLogsModal,

    // Email Editing
    isEditingEmail,
    setIsEditingEmail,
    editedEmail,
    setEditedEmail,
    handleEditEmailClick,
    handleCancelEditEmail,

    // Copy Link
    copySuccessMessage,
    setCopySuccessMessage,

    // Template Search
    templateSearchQuery,
    setTemplateSearchQuery,

    // File Action States
    duplicatingFileId,
    setDuplicatingFileId,
    deletingFileId,
    setDeletingFileId,
    addingProjectFromTemplateId,
    setAddingProjectFromTemplateId,
    isUploading,
    setIsUploading,

    // Metadata Modal
    selectedMetadata,
    showMetadataModal,
    handleShowMetadata,
    handleCloseMetadata,

    // Missing Font
    missingFontToAdd,
    setMissingFontToAdd,
    missingFontFile,
    setMissingFontFile,
  };
}
